import {
  PencilSquareIcon,
  PlusIcon,
  TagIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreatePrefLabel,
  useDeletePrefLabel,
  useEditPrefLabelDetails,
} from '@youshift/shared/hooks/mutations';
import { PreferenceLabel } from '@youshift/shared/types';
import {
  ColorName,
  colorNames,
  getRandomColor,
  LabelIcon,
  returnColor,
} from '@youshift/shared/utils';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { YSButton } from '../../../../components/Buttons';
import { LabelBadge } from '../../../../components/Calendars/LabelBadge';
import ColorPicker from '../../../../components/ColorPicker';
import DeleteConfirmationModal from '../../../../components/DeleteConfirmationModal';
import { EmptyState } from '../../../../components/EmptyState';
import IconPicker from '../../../../components/IconPicker';
import Modal from '../../../../components/Modal';
import Wrapper from '../../../../components/Wrapper';
import { useItrContext } from '../../../../layouts/IterationRootLayout/IterationRootLayout';
import LabelIconComponent from '../../../../components/LabelIconComponent';

export default function PrefLabelControlCenter() {
  const { idItr } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { prefLabels } = useItrContext();

  // State for modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // State for label form
  const [labelName, setLabelName] = useState('');
  const [labelColor, setLabelColor] = useState<ColorName>(getRandomColor());
  const [labelIcon, setLabelIcon] = useState<LabelIcon | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<PreferenceLabel | null>(
    null,
  );

  const resetForm = () => {
    setLabelName('');
    setLabelColor(getRandomColor());
    setLabelIcon(null);
    setSelectedLabel(null);
  };

  // Mutations
  const createPrefLabelMutation = useCreatePrefLabel(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assignmentTool', String(idItr)],
      });
      toast.success(t('manager.preferencesConfig.createLabelSuccess'));
      resetForm();
      setCreateModalOpen(false);
    },
  });

  const editPrefLabelMutation = useEditPrefLabelDetails(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assignmentTool', String(idItr)],
      });
      toast.success(t('manager.preferencesConfig.editLabelSuccess'));
      resetForm();
      setEditModalOpen(false);
    },
  });

  const deletePrefLabelMutation = useDeletePrefLabel(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['assignmentTool', String(idItr)],
      });
      toast.success(t('manager.preferencesConfig.eliminateLabelSuccess'));
      setDeleteModalOpen(false);
      setSelectedLabel(null);
    },
  });

  // Helper functions
  const handleCreatePrefLabel = () => {
    if (!labelName.trim() || !labelIcon) return;

    createPrefLabelMutation.mutate({
      id_itr: Number(idItr),
      new_pref_label: {
        name: labelName,
        icon: labelIcon,
        color: labelColor,
      },
    });
  };

  const handleEditPrefLabel = () => {
    if (!selectedLabel || !labelName.trim() || !labelIcon) return;

    editPrefLabelMutation.mutate({
      id_itr: Number(idItr),
      id_pref_label: selectedLabel.id_pref_label,
      pref_label: {
        name: labelName,
        icon: labelIcon,
        color: labelColor,
      },
    });
  };

  const handleDeletePrefLabel = () => {
    if (!selectedLabel) return;

    deletePrefLabelMutation.mutate({
      id_itr: Number(idItr),
      id_pref_label: selectedLabel.id_pref_label,
    });
  };

  const openEditModal = (label: PreferenceLabel) => {
    setSelectedLabel(label);
    setLabelName(label.name);
    setLabelColor(label.color);
    setLabelIcon(label.icon);
    setEditModalOpen(true);
  };

  const openDeleteModal = (label: PreferenceLabel) => {
    setSelectedLabel(label);
    setDeleteModalOpen(true);
  };

  return (
    <div>
      {prefLabels && Object.values(prefLabels).length > 0 ? (
        <div className="flex flex-wrap gap-2 items-center">
          {Object.values(prefLabels)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(label => (
              <div
                key={label.id_pref_label}
                className="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-sm font-medium text-gray-900 ring-1 ring-inset ring-gray-200 group relative"
                style={{
                  backgroundColor: returnColor(label.color, 50),
                }}
              >
                <LabelIconComponent icon={label.icon} className="w-4 h-4" />
                <span>{label.name}</span>
                <div className="hidden group-hover:flex absolute right-0 top-0 bottom-0 bg-gradient-to-l from-white/90 via-white/90 to-transparent pl-8 pr-1 items-center">
                  <button
                    onClick={() => openEditModal(label)}
                    className="p-1 rounded-full hover:bg-gray-200"
                    aria-label={t('generic.edit')}
                  >
                    <PencilSquareIcon className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => openDeleteModal(label)}
                    className="p-1 rounded-full hover:bg-gray-200"
                    aria-label={t('generic.delete')}
                  >
                    <TrashIcon className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            ))}
          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
            aria-label={t('manager.preferencesConfig.createLabel')}
          >
            <PlusIcon className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center text-gray-500">
          <TagIcon className="w-5 h-5 mr-2" />
          <span className="mr-2">
            {t('manager.preferencesConfig.noLabelsTitle')}
          </span>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            {t('manager.preferencesConfig.createFirstLabel')}
          </button>
        </div>
      )}

      {/* Create Label Modal */}
      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        size="md"
        editButtons
        handleSave={handleCreatePrefLabel}
        loadingSave={createPrefLabelMutation.isPending}
        disableSave={!labelName.trim() || !labelIcon}
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="labelName"
              className="block text-sm font-medium text-gray-700"
            >
              {t('generic.name')}
            </label>
            <input
              type="text"
              id="labelName"
              value={labelName}
              onChange={e => setLabelName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.color')}
            </label>
            <ColorPicker value={labelColor} onChange={setLabelColor} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.icon')}
            </label>
            <IconPicker value={labelIcon} onChange={setLabelIcon} />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.preview')}
            </label>
            <div className="flex items-center gap-2 p-3 border rounded-md">
              {labelIcon && (
                <LabelBadge icon={labelIcon} labelColor={labelColor} />
              )}
              <span className="font-medium">{labelName}</span>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Label Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        size="md"
        editButtons
        handleSave={handleEditPrefLabel}
        loadingSave={editPrefLabelMutation.isPending}
        disableSave={!labelName.trim() || !labelIcon}
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="editLabelName"
              className="block text-sm font-medium text-gray-700"
            >
              {t('generic.name')}
            </label>
            <input
              type="text"
              id="editLabelName"
              value={labelName}
              onChange={e => setLabelName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.color')}
            </label>
            <ColorPicker value={labelColor} onChange={setLabelColor} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.icon')}
            </label>
            <IconPicker value={labelIcon} onChange={setLabelIcon} />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.preview')}
            </label>
            <div className="flex items-center gap-2 p-3 border rounded-md">
              {labelIcon && (
                <LabelBadge icon={labelIcon} labelColor={labelColor} />
              )}
              <span className="font-medium">{labelName}</span>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Label Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeletePrefLabel}
        title={t('manager.preferencesConfig.deleteLabel')}
        description={t('manager.preferencesConfig.deleteWarning', {
          labelName: selectedLabel?.name,
        })}
        confirmationText={t('generic.delete')}
      />
    </div>
  );
}
